import * as React from 'react';
import { CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/core/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Calendar } from '@/shared/components/ui/Calendar';
import { floatingInputVariants, floatingLabelVariants } from './FloatingLabelInput';
import type { VariantProps } from 'class-variance-authority';
import { useDateFormat } from '@/shared/hooks/useDateFormat';
import { parseDatePickerValue } from '@/shared/lib/datetime/instant';

interface DateOnlyPickerProps extends VariantProps<typeof floatingInputVariants> {
  label: string;
  id: string;
  /** Floating calendar date as `yyyy-MM-dd` (no timezone). */
  value?: string | null;
  /** Emits `yyyy-MM-dd` (or `null` when cleared) — never an instant. */
  onChange: (value: string | null) => void;
  invalid?: boolean;
  disabled?: boolean;
  minDate?: Date;
  className?: string;
  'data-testid'?: string;
}

/**
 * Floating-label date picker for **date-only** values. Unlike `FloatingLabelDatePicker`
 * (which emits a UTC ISO instant via `toISOString()`), this emits the calendar day as a
 * literal `yyyy-MM-dd` string using `format(date, 'yyyy-MM-dd')` — no `toISOString`, so no
 * timezone day-shift. Use it for fields whose contract/storage is a floating date
 * (`date`), e.g. custom-field date values (opaque `yyyy-MM-dd`) or `date`-typed query params.
 */
export function DateOnlyPicker({
  label,
  id,
  value,
  onChange,
  invalid = false,
  disabled = false,
  minDate,
  variant,
  className,
  'data-testid': testId,
}: DateOnlyPickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const { dateFormat } = useDateFormat();

  const selectedDate = value ? parseDatePickerValue(value) : undefined;
  const isFloating = isOpen || !!value;

  const handleSelect = (date: Date | undefined) => {
    onChange(date ? format(date, 'yyyy-MM-dd') : null);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className="relative flex w-full items-center">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            aria-invalid={invalid}
            data-testid={testId}
            className={cn(
              floatingInputVariants({ variant }),
              'flex items-center gap-2 px-3 text-left',
              invalid && 'outline-destructive ring-2 ring-destructive/20',
              className,
            )}
          >
            <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-sm text-foreground">
              {value ? format(parseDatePickerValue(value), dateFormat) : ''}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            selected={selectedDate}
            onSelect={handleSelect}
            fromDate={minDate}
            mode="single"
          />
        </PopoverContent>
      </Popover>

      <label
        htmlFor={id}
        className={cn(
          floatingLabelVariants({ variant, floating: isFloating }),
          'pointer-events-none',
        )}
      >
        {label}
      </label>

      {value && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          tabIndex={-1}
          className="absolute right-3 flex items-center text-muted-foreground transition-colors hover:text-destructive"
          aria-label="Clear date"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
